import type { NonEmptyArray } from '../Types'

export class Queue {
    private lastPromise: Promise<unknown> = Promise.resolve()

    private async awaitNext<T> (asyncCall: () => Promise<T>, resolve: (value: T | PromiseLike<T>) => void, reject: (reason: unknown) => void): Promise<void> {
        try {
            resolve(await asyncCall())
        } catch (e: unknown) {
            reject(e)
        }
    }

    private async awaitSafely (previous: Promise<unknown>): Promise<void> {
        try {
            await previous
        } catch {
            // It is ok to hide errors here, only the code coverage tool is going to judge us
        }
    }

    /**
     * Adds an async callback to the async queue
     * This callback will only be executed after the previous callbacks
     *
     * @returns the promise that you gave to it through its callback, no errors are held
     */
    push<T> (asyncCall: () => Promise<T>): Promise<T> {
        const previous = this.lastPromise

        /**
         * Replace previous with new one
         */
        const current = new Promise<T>((resolve, reject) => {
            /**
             * Await for previous to finish
             */
            void this.awaitSafely(previous).then(() => {
                /**
                 * Then execute new one
                 */
                void this.awaitNext<T>(asyncCall, resolve, reject)
            })
        })

        /**
         * Replace previous with current
         */
        this.lastPromise = current

        return current
    }
}

/**
 * Simple pool that allows for async queued execution while iterating through a pool of queues
 */
export class QueuePool {
    private current = 0

    private readonly pool: Queue[]

    constructor (size: number) {
        this.pool = Array<null>(size)
            .fill(null)
            .map(() => new Queue())
    }

    private getQueue (): Queue {
        const [firstQueue] = this.pool.slice(this.current % this.pool.length) as NonEmptyArray<Queue>

        /** Move to the next one */
        this.current++

        return firstQueue
    }

    push<T> (asyncCall: () => Promise<T>): Promise<T> {
        return this.getQueue().push(asyncCall)
    }
}
